import React from 'react';
import styled from 'styled-components';
import { Observer, useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { LegacyBoardData } from '../../api/endpoints/boards';
import { Icon } from '../../concepts/resources/Icon';
import { useSubmitter } from '../../platform/Hook';
import { LabelValue } from '../../platform/types/LabelValue';
import { Colors, Input } from '../../ui';
import { LoadingFrame } from '../../ui/LoadingGuard';
import { MenuButton } from '../../ui/MenuButton';
import { useResolve } from '../Container';
import { DateBucketView } from '../boards/DateBucketView';
import { useScrollingCallback } from '../boards/Scroller';
import { SortMode } from '../common/Sorter';
import { BoardThumb } from '../dashboard/elements/BoardThumb';
import { ContentGrid, Flexer, Title } from '../dashboard/elements/LayoutElements';
import { XmasLightsDashboard } from '../occasions/xmas/XmasLightsDashboard';
import { PopupManager } from '../popups/PopupManager';
import { importBoardProcessPopup } from '../workspace/ImportBoardProcessPopup';
import { SortModeSelector } from '../workspace/SortModeSelector';
import { LegacyBoardModel } from './LegacyBoardModel';
import { LegacyBoardsActiveViewModel } from './LegacyBoardsActiveViewModel';

const { Item, ItemButton, ItemHeading, ItemBorder, ItemInfo } = ContentGrid;

const formatIfDate = (date: unknown): any => date instanceof Date ? date.toLocaleDateString() : date;

const modeOptions = [
    LabelValue('Just my boards', 'visited'),
    LabelValue('Everyone\'s boards', 'all'),
];

const sortOptions = [
    LabelValue('Last visited', 'lastSeenAt'),
    LabelValue('Last updated', 'updatedAt'),
    LabelValue('Date created', 'createdAt'),
    LabelValue('Alphabetical', 'label'),
];

const sortProps = [
    LabelValue('Visited', 'lastSeenAt'),
    LabelValue('Updated', 'updatedAt'),
    LabelValue('Created', 'createdAt'),
];

const sortModeToProp = (sort: SortMode) => sortProps.find(x => x.value === sort[0]);

const SortValueInfo = React.memo<{ board: LegacyBoardData, sort: SortMode }>(({ board, sort }) => {
    const value = formatIfDate(board[sort[0]]);
    if (!value) return null;
    switch (sort[0]) {
        // case 'relevance': return <RelevanceIndicator value={value} />;
        case 'lastSeenAt': return <span>Visited {value}</span>;
        case 'updatedAt': return <span>Updated {value}</span>;
        case 'createdAt': return <span>Created {value}</span>;
        default: return null;
    }
});

const BoardOptions = styled.div`
    display: none;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: .5rem;
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
`;

const BoardOptionButton = styled.div`
    padding: .25rem .5rem;
    color: white;
    font-weight: 450;
    font-size: 1.125rem;
    border-radius: .5rem;
    border: 1px solid white;
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, 0.3);
    }
`

const ItemBorderCustom = styled(ItemBorder)`
    height: 13rem;
    &:hover {
        box-shadow: none !important;
        ${BoardOptions} {
            display: flex;
        }
    }
    &:active {
        border: solid 0.1875rem #d2e1ec;
        box-shadow: none !important;
    }
`;

const BoardLayout = React.memo<{ label?: string, items: LegacyBoardModel[], sort: SortMode }>(({ label, items, sort }) => {
    if (!items.length) return null;

    const popups = useResolve(PopupManager);

    const onImport = (boardId: string) => () => {
        popups.invoke(importBoardProcessPopup({ list: [boardId] }));
    };
    const onView = (boardId: string) => () => {
        window.open(`/board/${boardId}?popup=false`, '_blank');
    };

    return <Flexer margin="0 0 1.5 0">
        <ContentGrid>
            {label && <Flexer margin="0 1.4 -.5">
                <Title flush>{label}</Title>
            </Flexer>}
            {items.map(b => <Observer key={`${b.id}:${sort.join(':')}`} store={b} render={bs =>
                <Item>
                    <ItemHeading>
                        {bs.label}
                    </ItemHeading>
                    <ItemBorderCustom>
                        <BoardThumb info={bs} />
                        <BoardOptions>
                            {!!b.children.length && <BoardOptionButton onClick={onView(b.children[0])}>View Import</BoardOptionButton>}
                            {!b.children.length && <BoardOptionButton onClick={onImport(b.id)}>Import To V2</BoardOptionButton>}
                            <BoardOptionButton onClick={onView(b.id)}>View Original</BoardOptionButton>
                        </BoardOptions>
                    </ItemBorderCustom>
                    <ItemInfo>
                        <Flexer grow flex="row-reverse" justify="space-between">
                            <SortValueInfo board={bs} sort={sort} />
                            {!!b.children.length && <span><Icon src="deqo://icons/hero-mini/check" size="1" /> Imported</span>}
                        </Flexer>
                    </ItemInfo>
                </Item>
            } />)}
        </ContentGrid>
    </Flexer>;
});

const TopLevelBoardLayout = React.memo<{ items: LegacyBoardModel[], sort: SortMode }>(({ items, sort }) =>
    sort[0].endsWith('At') ? (
        <DateBucketView items={items} using={sortModeToProp(sort)} order={sort[1]} render={x => {
            return <BoardLayout label={x.label} items={x.items as any} sort={sort} />;
        }
        } />
    ) : (
        <BoardLayout items={items} sort={sort} />
    )
);

const EmptyLayout = () =>
    <Flexer inset flex center pointerOff gap=".5">
        <Title color={Colors.gray400} flush size="3xl">You&apos;ve not created or joined any boards yet!</Title>
        <Title color={Colors.gray400} flush size="lg">When you do, they will show up here.</Title>
    </Flexer>;

const NoResultsLayout = () =>
    <Flexer inset flex center pointerOff gap=".5">
        <Title color={Colors.gray400} flush size="3xl">We couldn&apos;t find anything matching your search.</Title>
        <Title color={Colors.gray400} flush size="lg">Not sure what else to say really...</Title>
    </Flexer>;

type ViewActions =
    readonly ['search', string] |
    readonly ['sort', SortMode] |
    readonly ['extend'];

export const LegacyBoardsView = () => {

    const model = useResolve(LegacyBoardsActiveViewModel);

    const [onAction, working] = useSubmitter(async ([action, param]: ViewActions) => {
        if (action === 'search') return await model.refresh({ search: param });
        if (action === 'sort') return await model.refresh({ sort: param });
        if (action === 'extend') return await model.extend();
    }, []);

    const { items, search, sort } = useObserver(model);

    useScrollingCallback(({ scrollTop, scrollHeight, clientHeight }) => {
        if (working) return;
        const rem = Math.abs(scrollHeight - (scrollTop + clientHeight));
        const hit = rem >= 0 && rem < 50;
        if (hit) onAction(['extend']);
    }, [onAction, working]);

    return <Flexer>
        <XmasLightsDashboard />
        <Flexer flex="row" align="center" justify="space-between" margin="0 0 3" >
            <Flexer width="20">
                <Input autoFocus placeholder="Search" value={search} onChange={x => onAction(['search', x])} />
            </Flexer>
            <Flexer flex="row" align="center">
                <Flexer flex="row" width="10">
                    {!!search ? (
                        <MenuButton disabled label="Relevance" />
                    ) : (
                        <SortModeSelector options={sortOptions} value={sort} onChange={x => onAction(['sort', x])} />
                    )}
                </Flexer>
            </Flexer>
        </Flexer>
        {!working && items.length === 0 && (
            !!search ? <NoResultsLayout /> : <EmptyLayout />
        )}
        <TopLevelBoardLayout {...{ items, sort: !search ? sort : ['relevance', 'desc'] }} />
        {working && (
            <LoadingFrame />
        )}
    </Flexer>;
};
